import React, { useMemo, useState } from 'react';
import {
    Close,
    ReceiptLong,
    Share,
    ContentCopy,
    Person,
    Payment,
    Storefront,
    CheckCircle,
    Schedule,
    ErrorOutline,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { buildTransactionDetail, formatMoney, formatDateTime } from '../../../utils/transactionDetail';
import { printReceipt, shareTransaction } from '../../../utils/transactionReceipt';

const TONES = {
    success: { icon: CheckCircle, hero: 'from-emerald-500 to-teal-500' },
    pending: { icon: Schedule, hero: 'from-amber-500 to-orange-500' },
    failed: { icon: ErrorOutline, hero: 'from-rose-500 to-red-500' },
};

const Row = ({ label, value, mono = false }) => (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 last:border-b-0">
        <span className="text-sm text-slate-500 shrink-0">{label}</span>
        <span className={`text-sm font-semibold text-slate-900 text-right break-all ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
);

const Section = ({ icon: Icon, title, children }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
            <Icon sx={{ fontSize: 16 }} /> {title}
        </p>
        {children}
    </div>
);

const TransactionModal = ({ transaction, onClose }) => {
    const { selectedOrganization } = useAuth();
    const detail = useMemo(() => buildTransactionDetail(transaction), [transaction]);
    const [feedback, setFeedback] = useState(null);
    const tone = TONES[detail.statusTone] || TONES.pending;
    const StatusIcon = tone.icon;

    const flash = (text) => {
        setFeedback(text);
        setTimeout(() => setFeedback(null), 2500);
    };

    const copyReference = async () => {
        try {
            await navigator.clipboard.writeText(detail.reference);
            flash('Référence copiée');
        } catch {
            flash('Copie impossible');
        }
    };

    const handleShare = async () => {
        try {
            const result = await shareTransaction(detail, selectedOrganization);
            if (result === 'copied') flash('Détails copiés, prêts à coller');
        } catch {
            flash('Partage impossible');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
            <div className="relative w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-3xl bg-slate-50 shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* En-tête : montant et statut */}
                <div className={`relative bg-gradient-to-br ${tone.hero} px-6 pt-5 pb-6 text-white`}>
                    <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl hover:bg-white/15" aria-label="Fermer">
                        <Close />
                    </button>
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
                        {detail.isWithdrawal ? 'Détails du retrait' : 'Détails de la transaction'}
                    </p>
                    <p className="mt-3 text-4xl font-bold tracking-tight tabular-nums">{formatMoney(detail.amount, detail.currency)}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">
                            <StatusIcon sx={{ fontSize: 16 }} /> {detail.statusLabel}
                        </span>
                        {detail.channel && <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium">{detail.channel}</span>}
                        <span className="text-xs text-white/85">{formatDateTime(detail.completedAt || detail.requestedAt)}</span>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Référence commune au reçu */}
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800/80">Référence (identique au reçu)</p>
                            <p className="mt-1 font-mono text-lg font-bold text-amber-900 break-all">{detail.reference}</p>
                            {detail.receiptId && <p className="mt-1 text-xs text-amber-900/70 break-all">ID du reçu : {detail.receiptId}</p>}
                        </div>
                        <button type="button" onClick={copyReference} className="p-2 rounded-xl text-amber-800 hover:bg-amber-100 shrink-0" title="Copier la référence">
                            <ContentCopy fontSize="small" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Section icon={Person} title={detail.isWithdrawal ? 'Bénéficiaire' : 'Client'}>
                            <Row label="Nom" value={detail.payerName} />
                            <Row label="Téléphone" value={detail.payerPhone} mono />
                            <Row label="Libellé" value={detail.label} />
                        </Section>
                        <Section icon={Payment} title={detail.isWithdrawal ? 'Retrait' : 'Paiement'}>
                            <Row label={detail.isWithdrawal ? 'Moyen de retrait' : 'Méthode'} value={detail.method} />
                            <Row label="Opérateur" value={detail.provider} />
                            <Row label="Réf." value={detail.operatorRef} mono />
                        </Section>
                        {(detail.terminal || detail.cashier || detail.caisse) && (
                            <Section icon={Storefront} title="Point de vente">
                                <Row label="Terminal" value={detail.terminal} mono />
                                {detail.caisse && <Row label="Caisse" value={detail.caisse} />}
                                <Row label="Caissier" value={detail.cashier} />
                            </Section>
                        )}
                        <Section icon={ReceiptLong} title="Montants">
                            <Row label="Montant" value={formatMoney(detail.amount, detail.currency)} />
                            <Row label="Frais" value={formatMoney(detail.fees, detail.currency)} />
                            <Row label={detail.isWithdrawal ? 'Net versé' : 'Net perçu'} value={formatMoney(detail.netAmount, detail.currency)} />
                        </Section>
                    </div>
                </div>

                <div className="border-t border-slate-200 bg-white p-4 flex flex-col sm:flex-row gap-3">
                    <button type="button" onClick={() => printReceipt(detail, selectedOrganization)}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 px-4 py-3 font-medium text-slate-700 hover:bg-slate-50">
                        <ReceiptLong fontSize="small" /> Télécharger le reçu (PDF)
                    </button>
                    <button type="button" onClick={handleShare}
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 font-semibold text-white hover:from-amber-600 hover:to-orange-600">
                        <Share fontSize="small" /> Partager
                    </button>
                </div>
                {feedback && (
                    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 px-4 py-2 text-sm text-white shadow-lg">{feedback}</div>
                )}
            </div>
        </div>
    );
};

export default TransactionModal;
